/**
 * PlayerDetailRight View Utility Functions
 *
 * Helper functions for computing CSS classes in the PlayerDetailRightView component.
 * These functions reduce cognitive complexity by extracting conditional styling logic.
 * @module components/PlayerDetailRight/utils
 */

/**
 * Props for category button class computation
 */
interface CategoryButtonClassProps {
  /** Whether category is selected */
  readonly isSelected: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
}

/**
 * Props for category text class computation
 */
interface CategoryTextClassProps {
  /** Whether category is selected */
  readonly isSelected: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether screen is small */
  readonly isSmallScreen: boolean;
}

/**
 * Props for heading class computation
 */
interface HeadingClassProps {
  /** Whether screen is small */
  readonly isSmallScreen: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
}

/**
 * Get category button classes based on selection and theme
 * @param props - Selection and theme state
 * @returns Button className string
 */
export function getCategoryButtonClasses(
  props: CategoryButtonClassProps
): string {
  const { isSelected, isDark } = props;
  const baseClasses = "rounded-sm border p-4 py-2";
  if (isSelected) {
    return `${baseClasses} border-status-info bg-status-info/20`;
  }
  const bgClass = isDark ? "bg-background-gray" : "bg-surface-muted";
  return `${baseClasses} border-surface-strong ${bgClass}`;
}

/**
 * Get category text classes based on selection, theme, and screen size
 * @param props - Selection, theme, and screen size
 * @returns Text className string
 */
export function getCategoryTextClasses(props: CategoryTextClassProps): string {
  const { isSelected, isDark, isSmallScreen } = props;
  const sizeClass = isSmallScreen ? "" : "text-title-sm";
  const colorClass = isSelected
    ? "text-status-info"
    : isDark
      ? "text-white"
      : "text-content-primary";
  return `${sizeClass} uppercase ${colorClass}`;
}

/**
 * Get heading classes based on screen size and theme
 * @param props - Screen size and theme
 * @returns Heading className string
 */
export function getHeadingClasses(props: HeadingClassProps): string {
  const { isSmallScreen, isDark } = props;
  const colorClass = isDark ? "text-white" : "text-black";
  if (isSmallScreen) {
    return colorClass;
  }
  return `text-title ${colorClass}`;
}
